import React from "react"
import { useTranslation } from "react-i18next"
import * as XLSX from "xlsx"
import i18next from "i18next"
import dateService from "services/date"

import { DownloadIcon } from "components/Elements/Icons"
import { EntityBOCollection } from "models"

type ExportDataProps = {
	selectedDatas: any
	datas: EntityBOCollection[]
	disabled: boolean
	isAdminOCode: boolean
}

export const ExportData = ({ selectedDatas, datas, disabled, isAdminOCode }: ExportDataProps) => {
	const { t } = useTranslation()

	const exportPartners = (selectedDatas) => {
		// Filter bikes'list from original list
		const selectedPartners = datas.filter((partner) => {
			return selectedDatas.some((selectedPartner) => {
				return partner.client_number === selectedPartner.values.client_number
			})
		})

		if (selectedPartners) {
			// Data for ocode admin
			const selectedPartnersForAdmin = selectedPartners.map((partner) => {
				const newPartner = {
					create_date: dateService.formatDate(partner.create_date, "dd/MM/y"),
					social_reason: partner.social_reason,
					commercial_name: partner.commercial_name,
					siret: partner.siret,
					address: partner.address,
					postal_code: partner.postal_code,
					town: partner.town,
					client_number: partner.client_number,
					email: partner.email,
					prenom_representant_legal: partner.prenom_representant_legal,
					nom_representant_legal: partner.nom_representant_legal,
					phone: partner.phone,
					operators: partner.operators.toString(),
					mfc_client_number: partner.mfc_client_number, // N° client MFC
					gender_mfc: partner.gender_mfc, // Spécificité du partenaire MFC
					gender: t(partner.gender), // Typologie de clients
					isFranchise: partner.isFranchise, // Appartient-il à une chaîne ou franchise ?
					structureChainName: partner.structureChainName, // Nom de la chaîne
					isFubMember: partner.isFubMember, //  Êtes vous membre de la FUB?,
					isFleetMFC: partner.isFleetMFC, //  Avez vous activez la flotte pour mfc ?
					isFleetBc: partner.isFleetBC, //  Avez vous activez la flotte pour bc ?
					isG8Activate: partner.isG8Activate, //  G8 est activé ?
					firstG8Activation: partner.firstG8Activation, // Date de la premiere activation G8
					lastG8Activation: partner.lastG8Activation, // Date de la derniere activation G8
				}

				return newPartner
			})

			// Data for other users
			const selectedPartnersWithFilteredData = selectedPartners.map((partner) => {
				const newPartner = {
					create_date: dateService.formatDate(partner.create_date, "dd/MM/y"),
					social_reason: partner.social_reason,
					commercial_name: partner.commercial_name,
					siret: partner.siret,
					address: partner.address,
					postal_code: partner.postal_code,
					town: partner.town,
					client_number: partner.client_number,
					email: partner.email,
					prenom_representant_legal: partner.prenom_representant_legal,
					nom_representant_legal: partner.nom_representant_legal,
					phone: partner.phone,
					isFubMember: partner.isFubMember, // Êtes vous membre de la FUB?
					isFleetMFC: partner.isFleetMFC, //  Avez vous activez la flotte pour mfc ?
					isFleetBc: partner.isFleetBC, //  Avez vous activez la flotte pour bc ?
					isG8Activate: partner.isG8Activate, //  G8 est activé ?
					firstG8Activation: partner.firstG8Activation, // Date de la premiere activation G8
					lastG8Activation: partner.lastG8Activation, // Date de la derniere activation G8
				}

				return newPartner
			})

			// prepare xlsx
			const wb = XLSX.utils.book_new()

			// Heading with translations
			const Heading = [
				i18next.t("edit.state_of_ref", { ns: "partner" }),
				i18next.t("profil.social_reason", { ns: "partner" }),
				i18next.t("profil.commercial_name", { ns: "partner" }),
				i18next.t("profil.siret", { ns: "partner" }),
				i18next.t("store.address", { ns: "partner" }),
				i18next.t("profil.postal_code", { ns: "partner" }),
				i18next.t("profil.town", { ns: "partner" }),
				i18next.t("edit.declarator_number", { ns: "partner" }),
				i18next.t("profil.e-mail", { ns: "partner" }),
				i18next.t("profil.firstname", { ns: "team" }),
				i18next.t("profil.name", { ns: "team" }),
				i18next.t("profil.phone", { ns: "partner" }),
			]

			const AdminHeading = [
				i18next.t("edit.operator_for", { ns: "partner" }) + "(s)",
				i18next.t("profil.client_number", { ns: "partner" }) + " MFC",
				i18next.t("specificity", { ns: "partner" }) + " MFC",
				i18next.t("type", { ns: "partner" }),
				i18next.t("isFranchise", { ns: "partner" }),
				i18next.t("store.structure_chain_name", { ns: "partner" }),
			]

			const LastCommonHeading = [
				i18next.t("edit.afub-member", { ns: "partner" }),
				i18next.t("edit.has_fleet_mfc", { ns: "partner" }),
				i18next.t("edit.has_fleet_bc", { ns: "partner" }),
				i18next.t("edit.G8_activate", { ns: "partner" }),
				i18next.t("edit.G8_first_activate", { ns: "partner" }),
				i18next.t("edit.G8_last_activate", { ns: "partner" }),
			]

			const xlsxHeading = isAdminOCode
				? [...Heading, ...AdminHeading, ...LastCommonHeading]
				: [...Heading, ...LastCommonHeading]

			const fname = "export-liste-partenaires"

			const ws = XLSX.utils.json_to_sheet(
				isAdminOCode ? selectedPartnersForAdmin : selectedPartnersWithFilteredData
			)

			XLSX.utils.sheet_add_json(ws, [], {
				origin: "A1",
				header: xlsxHeading,
			})

			XLSX.utils.book_append_sheet(
				wb,
				ws,
				i18next.t("list.export_partners", { ns: "partner" })
			)
			XLSX.writeFile(wb, fname + ".xlsx")
		}
	}

	return (
		<button
			type="button"
			onClick={() => exportPartners(selectedDatas)}
			className="c-btn c-btn--primary px-7"
			disabled={disabled}
		>
			<DownloadIcon size={20} className="u-mg-right-s" />
			{t("list.export_partners", { ns: "partner" })} ({selectedDatas.length})
		</button>
	)
}
